import React, { useState, useEffect } from 'react';
import { FeatureExtractionAPI } from '../../../../api/client';

const DataSourceSection = ({ formData, isEditing, setFormData }) => {
  const [readyJobs, setReadyJobs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    const fetchReadyJobs = async () => {
      if (!isEditing) return;

      setLoading(true);
      try {
        const jobs = await FeatureExtractionAPI.ready();
        setReadyJobs(jobs);
      } catch (e) {
        setError('Failed to load ready feature extraction jobs');
      } finally {
        setLoading(false);
      }
    };

    fetchReadyJobs();
  }, [isEditing]);

  const handleJobSelection = (jobId) => {
    if (!jobId) {
      setFormData(prev => ({
        ...prev,
        selected_feature_extraction_job: null,
        data_source: null
      }));
      return;
    }

    const job = readyJobs.find(j => j.id.toString() === jobId);
    if (!job) return;

    const dataSource = {
      base_path: "data/",
      master_dir: `${job.slug}/`,
      demographics_file: "demographics.csv",
      order_column: "id",
      metabolites_dir: "metabolites/"
    };

    setFormData(prev => ({
      ...prev,
      selected_feature_extraction_job: job,
      data_source: dataSource
    }));
  };

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
      <div className="mb-4">
        <h2 className="text-lg font-semibold text-gray-900">Data Source</h2>
        <p className="text-sm text-gray-600 mt-1">
          Select a ready feature extraction job to use as your data source
        </p>
      </div>

      {isEditing ? (
        <div>
          {loading && (
            <div className="text-sm text-gray-500 mb-4">Loading ready jobs...</div>
          )}

          {error && (
            <div className="text-sm text-red-600 mb-4">{error}</div>
          )}

          {!loading && !error && readyJobs.length === 0 && (
            <div className="text-sm text-gray-500 mb-4">No ready feature extraction jobs available</div>
          )}

          {!loading && readyJobs.length > 0 && (
            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Select Feature Extraction Job <span className="text-red-500">*</span>
              </label>
              <div className="relative">
                <select
                  className="w-full border rounded px-3 py-2 appearance-none bg-white pr-8 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  value={formData.selected_feature_extraction_job?.id || ''}
                  onChange={(e) => handleJobSelection(e.target.value)}
                  disabled={!isEditing}
                >
                  <option value="">Select a feature extraction job...</option>
                  {readyJobs.map((job) => (
                    <option key={job.id} value={job.id}>
                      {job.name} (ID: {job.id})
                    </option>
                  ))}
                </select>
                <div className="absolute inset-y-0 right-0 flex items-center pr-2 pointer-events-none">
                  <svg className="h-4 w-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </div>
              </div>

              {/* Show selected job details below dropdown */}
              {formData.selected_feature_extraction_job && (
                <div className="mt-3 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                  <div className="text-sm">
                    <p className="font-medium text-blue-900 mb-1">{formData.selected_feature_extraction_job.name}</p>
                    <p className="text-blue-700">
                      <span className="font-medium">Slug:</span> {formData.selected_feature_extraction_job.slug}
                    </p>
                    <p className="text-blue-700">
                      <span className="font-medium">GCS Path:</span> {formData.selected_feature_extraction_job.gcs_path}
                    </p>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      ) : (
        <div>
          {formData.selected_feature_extraction_job ? (
            <div className="p-4 bg-gray-50 rounded-lg">
              <h4 className="font-medium text-gray-900 mb-2">Selected Job</h4>
              <div className="text-sm text-gray-600">
                <p><span className="font-medium">Name:</span> {formData.selected_feature_extraction_job.name}</p>
                <p><span className="font-medium">ID:</span> {formData.selected_feature_extraction_job.id}</p>
                <p><span className="font-medium">Slug:</span> {formData.selected_feature_extraction_job.slug}</p>
                <p><span className="font-medium">GCS Path:</span> {formData.selected_feature_extraction_job.gcs_path}</p>
              </div>
            </div>
          ) : (
            <div className="text-sm text-gray-500">No data source selected</div>
          )}
        </div>
      )}

      {/* Data Source Configuration Display */}
      {formData.data_source && (
        <div className="mt-6">
          <h3 className="text-sm font-medium text-gray-700 mb-3">Data Source Configuration</h3>
          <div className="bg-gray-50 rounded-lg p-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
              <div>
                <span className="font-medium text-gray-600">Base Path:</span>
                <div className="font-mono text-gray-900">{formData.data_source.base_path}</div>
              </div>
              <div>
                <span className="font-medium text-gray-600">Master Dir:</span>
                <div className="font-mono text-gray-900">{formData.data_source.master_dir}</div>
              </div>
              <div>
                <span className="font-medium text-gray-600">Demographics File:</span>
                <div className="font-mono text-gray-900">{formData.data_source.demographics_file}</div>
              </div>
              <div>
                <span className="font-medium text-gray-600">Order Column:</span>
                <div className="font-mono text-gray-900">{formData.data_source.order_column}</div>
              </div>
              <div className="md:col-span-2">
                <span className="font-medium text-gray-600">Metabolites Dir:</span>
                <div className="font-mono text-gray-900">{formData.data_source.metabolites_dir}</div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Info Panel */}
      <div className="mt-6 p-4 bg-blue-50 border border-blue-200 rounded-md">
        <div className="flex items-start space-x-2">
          <svg className="w-5 h-5 text-blue-600 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <div>
            <h4 className="text-sm font-medium text-blue-900">Data Source Selection</h4>
            <p className="text-sm text-blue-700 mt-1">
              Select a completed feature extraction job to use as your inference data source. The configuration will be automatically generated based on your selection.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DataSourceSection;
